import { useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@coingate/ui/dist/components/Button';
import { FormCheckbox } from '@coingate/ui/dist/components/FormCheckbox';
import { FormTextInput } from '@coingate/ui/dist/components/FormTextInput';
import { mapToInputProps } from '@coingate/ui/dist/lib/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Request } from '@javascript/helpers';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@shared/components/TurnstileWidget';
import { getErrorMessage } from '@shared/errors';
import { useAnalytics } from '@shared/hooks/useAnalytics';

import { GetOauthURL } from '@javascript/helpers';
import { PRIVACY_POLICY_LINK, TERMS_OF_SERVICE_LINK } from '../../constants';
import type { NewUserFormType } from '../../validations';
import { createNewUserFormSchema } from '../../validations';

export type ConsentData = {
  email?: string;
  termsAccepted: boolean;
  newsletterAccepted: boolean;
}

const ConsentStep = ({
  signUpMethod,
  invitationId,
  onContinue,
}: {
  signUpMethod: string;
  invitationId?: string | null;
  onContinue: (data: ConsentData) => void;
}) => {
  const { trackEvent } = useAnalytics('authentication');

  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [newsletterAccepted, setNewsletterAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTurnstileReady, setIsTurnstileReady] = useState(true);

  const NewUserFormSchema = useMemo(
    () => createNewUserFormSchema(signUpMethod),
    [signUpMethod]
  );

  const methods = useForm<NewUserFormType>({
    resolver: zodResolver(NewUserFormSchema),
  });
  const {
    control,
    handleSubmit,
    setError,
  } = methods;

  const handleContinue = async (data: NewUserFormType) => {
    trackEvent('signup_consent_submit', { method: signUpMethod });

    if (!termsAccepted) {
      trackEvent('signup_consent_terms_error');
      setTermsError(true);
      return;
    }

    if (signUpMethod === 'google') {
      trackEvent('signup_consent_google_redirect');

      setIsSubmitting(true);
      const params = new URLSearchParams({
        entity_type: 'person',
        sign_up_url: window.location.href,
        hasAgreedToTerms: String(termsAccepted),
        hasAgreedToPrivacy: String(termsAccepted),
        isMarketingOptIn: String(newsletterAccepted),
        ...(invitationId && { invitation_id: invitationId }),
      });

      window.location.href = GetOauthURL(params.toString());
      return;
    }

    if (signUpMethod === 'email') {
      setIsSubmitting(true);

      const turnstileToken = await turnstileRef.current!.getResponsePromise();

      const values = {
        user: {
          sign_up_url: window.location.href,
          email: data.email,
          agreed_to_terms: termsAccepted,
          agreed_to_privacy: termsAccepted,
          marketing_opt_in: newsletterAccepted,
          terms: true,
        }
      }

      Request('/users', 'POST', { body: { ...values, 'cf-turnstile-response': turnstileToken } })
        .then((_response) => {
          trackEvent('signup_consent_submit_success');

          onContinue({
            email: data.email,
            termsAccepted: termsAccepted,
            newsletterAccepted: newsletterAccepted
          });
        })
        .catch((err) => {
          trackEvent('signup_consent_submit_failed', { error: err });

          const message = getErrorMessage(err, 'email');

          if (message) {
            setError('email', { message });
          }

          const resetTurnstile = err?.error_code !== 'turnstile_incomplete';

          if (resetTurnstile) {
            turnstileRef.current?.reset();
          }

          setIsSubmitting(false);
        })
    }
  };

  return (
    <form onSubmit={handleSubmit(handleContinue)}>
      <div className="flex flex-col">
        <div className="flex flex-col gap-6">
          {signUpMethod === 'email' && (
            <div className="text-left px-2">
              <Controller
                name="email"
                control={control}
                render={(controllerProps) => (
                  <FormTextInput
                    {...mapToInputProps(controllerProps)}
                    label="Business email address"
                    inputMode="email"
                    onFocus={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                  />
                )}
              />
            </div>
          )}

          {/* text-left/px-2 mirror the email field above: the step wrapper centres text. */}
          <div className="flex flex-col gap-2 text-left px-2">
            <FormCheckbox
              id="terms-accepted"
              name="termsAccepted"
              data-testid="terms-accepted"
              value={termsAccepted}
              onChange={(checked) => {
                setTermsAccepted(checked === true);
                if (checked) setTermsError(false);
              }}
              error={termsError ? 'Please accept terms to continue.' : undefined}
              label={
                <>
                  I agree to CoinGate's{' '}
                  <a
                    className="text-grey-600 underline hover:text-grey-600"
                    data-test="terms-of-service-link"
                    target="_blank"
                    rel="noreferrer"
                    href={TERMS_OF_SERVICE_LINK}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>{' '}
                  and acknowledge the{' '}
                  <a
                    className="text-grey-600 underline hover:text-grey-600"
                    data-test="privacy-policy-link"
                    target="_blank"
                    rel="noreferrer"
                    href={PRIVACY_POLICY_LINK}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                  .
                </>
              }
            />

            <FormCheckbox
              id="newsletter-accepted"
              name="newsletterAccepted"
              data-testid="newsletter-accepted"
              value={newsletterAccepted}
              onChange={(checked) => setNewsletterAccepted(checked === true)}
              label="I would like to receive CoinGate newsletter."
            />
          </div>

          {signUpMethod === 'email' && <TurnstileWidget ref={turnstileRef} onTokenChange={(token) => setIsTurnstileReady(token !== null)} />}

          <Button
            data-testid="create-an-account"
            className="w-full text-base font-semibold text-white"
            size="lg"
            variant="primary"
            type="submit"
            disabled={isSubmitting || !isTurnstileReady}
          >
            {signUpMethod === 'google' && (
              <>
                <img src="/google-fill.svg" />
                Continue with Google
              </>
            )}
            {signUpMethod === 'email' && (
              <>
                Create an account
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default ConsentStep
